// ─────────────────────────────────────────────────────────────────────────────
// useAcceptInvite — token extraction, form state, validation, the accept call,
// and the post-success redirect (now with a cleaned-up timer, unlike the old
// page which leaked the setTimeout).
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../services/api";
import { validatePassword, validatePasswordMatch } from "../utils/validators";

export function useAcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [form, setForm] = useState({ firstName: "", lastName: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  // No token → bounce to login.
  useEffect(() => {
    if (!token) navigate("/login", { replace: true });
  }, [token, navigate]);

  // Redirect after success, cleaning the timer on unmount.
  useEffect(() => {
    if (!done) return undefined;
    const id = setTimeout(() => navigate("/login", { replace: true }), 3000);
    return () => clearTimeout(id);
  }, [done, navigate]);

  const setField = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = async () => {
    const passwordError = validatePassword(form.password) || validatePasswordMatch(form.password, form.confirm);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post(`/users/invitations/${token}/accept`, {
        password: form.password,
        firstName: form.firstName.trim() || undefined,
        lastName: form.lastName.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(err.message || "Failed to accept invitation. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return { token, form, setField, loading, error, done, submit };
}

export default useAcceptInvite;
